import React from 'react';
import { Card, Typography, Divider } from 'antd';
import { IconInfoCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { BookingRentalMode } from '../interfaces/heavyDutyMachinery/IBooking';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatFullDate } from '../helpers/dateFormatHelper';
import { formatCurrency } from '../helpers/currencyHelper';
import { isDailyRentalMode, lastRentalDay } from '../helpers/rentalPeriodHelper';
import { TEAL, SURFACE_1_SOFT } from '../theme/creamyTokens';

const { Text } = Typography;

/** A ServiceAddOn folded into its parent Service's own BookingLine.Amount --
 * never a BookingLine of its own (see that entity's own doc comment), just a
 * price detail shown nested under the Service line that includes it. */
export interface IBookingSummarySubLine {
  key: string;
  label: string;
  amount: number;
}

/** One BookingLine's own worth of a checkout summary -- provider + amount,
 * the same concept whether it's a Machinery's base rental, an Operator, or a
 * Service (standalone or attached to a Machinery booking). */
export interface IBookingSummaryLine {
  key: string;
  label: string;
  /** Who this line is actually owed to -- e.g. an operator's or a Service's
   * own provider display name. Omitted for a line whose payee is already
   * obvious from context (the base rental line, paid to this same listing's
   * own host, shown as the header just above). */
  providerLabel?: string;
  amount: number;
  subLines?: IBookingSummarySubLine[];
}

export interface IBookingSummaryData {
  /** Space or Service title, shown as a subheading under the card title. */
  headerTitle: string;
  startAt: string;
  endAt: string;
  rentalMode: BookingRentalMode;
  hostCurrencyCode: string;
  guestCurrencyCode: string;
  totalPrice: number;
  guestChargeAmount: number;
  lines: IBookingSummaryLine[];
  // A hold, not a charge -- see IPendingPayment's own copy of this field.
  depositAmount?: number;
}

/**
 * What's about to be charged -- shown above the payment form itself so a
 * guest isn't asked to hand over payment details with nothing on screen
 * saying what for. The one shared multi-line summary for every checkout
 * flow in the app: a standalone Service booking (ServiceDetail) and a
 * Machinery booking with zero or more attached Services
 * (HeavyDutyMachineryBookingWidget) both build the same `lines` shape here
 * -- provider + amount per BookingLine, with a Service's own selected
 * ServiceAddOns nested as that line's subLines -- rather than each flow
 * carrying its own bespoke summary UI.
 *
 * The breakdown section only renders once there's actually something to
 * break down (2+ lines, or a single line with its own subLines) -- a plain
 * single-line booking (e.g. dry-hire, no extras) keeps showing just the
 * Total, same as before this component existed.
 */
export const BookingLinesSummary: React.FC<{ summary: IBookingSummaryData; language: string }> = ({ summary, language }) => {
  const { t } = useTranslation();
  const start = dayjs(summary.startAt);
  const end = dayjs(summary.endAt);

  const dateLines: React.ReactNode =
    isDailyRentalMode(summary.rentalMode) ? (
      // Last day actually rented, not the exclusive endAt -- see
      // rentalPeriodHelper.
      <>
        <Text>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_FIRST_DAY_LABEL)}: {formatFullDate(start, language)}
        </Text>
        <br />
        <Text>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_LAST_DAY_LABEL)}: {formatFullDate(lastRentalDay(end), language)}
        </Text>
      </>
    ) : summary.rentalMode === 'DayPass' ? (
      <Text>{formatFullDate(start, language)}</Text>
    ) : (
      <Text>
        {formatFullDate(start, language)}, {start.format('HH:mm')} – {end.format('HH:mm')}
        {!start.isSame(end, 'day') && <> ({formatFullDate(end, language)})</>}
      </Text>
    );

  const showBreakdown = summary.lines.length > 1 || summary.lines.some((l) => l.subLines && l.subLines.length > 0);

  return (
    <Card bordered={false} style={{ borderRadius: 18, background: SURFACE_1_SOFT, marginBottom: 16 }}>
      <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_PAYMENT_SUMMARY_TITLE)}
      </Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
        {summary.headerTitle}
      </Text>
      {dateLines}
      {showBreakdown && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          {summary.lines.map((line) => (
            <div key={line.key} style={{ marginBottom: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">
                  {line.label}
                  {line.providerLabel ? ` (${line.providerLabel})` : ''}
                </Text>
                <Text type="secondary">{formatCurrency(line.amount, summary.hostCurrencyCode, language)}</Text>
              </div>
              {line.subLines && line.subLines.length > 0 && (
                <div style={{ marginLeft: 16 }}>
                  {line.subLines.map((sub) => (
                    <div key={sub.key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {sub.label}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatCurrency(sub.amount, summary.hostCurrencyCode, language)}
                      </Text>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Divider style={{ margin: '8px 0' }} />
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_PRICE_LABEL)}</Text>
        <div style={{ textAlign: 'right' }}>
          {/* The actual amount Stripe will charge -- guestChargeAmount is
           * always in guestCurrencyCode, which only differs from
           * hostCurrencyCode once a guest has set their own
           * PreferredCurrencyCode. Every line above stays in
           * hostCurrencyCode (what it actually is), so this is deliberately
           * the only place totalPrice itself isn't shown as the headline
           * number. */}
          <Text strong style={{ fontSize: 18, color: TEAL }}>
            {formatCurrency(summary.guestChargeAmount, summary.guestCurrencyCode, language)}
          </Text>
          {summary.guestCurrencyCode !== summary.hostCurrencyCode && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_PRICE_HOST_CURRENCY_NOTICE, {
                amount: formatCurrency(summary.totalPrice, summary.hostCurrencyCode, language),
              })}
            </Text>
          )}
        </div>
      </div>
      {/* Deliberately its own section, below the Total line -- a hold, not
       * a charge, so it never joins the breakdown above or totalPrice. */}
      {summary.depositAmount != null && summary.depositAmount > 0 && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px dashed ${TEAL}33` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DEPOSIT_AMOUNT_LABEL)}</Text>
            <Text type="secondary">{formatCurrency(summary.depositAmount, summary.hostCurrencyCode, language)}</Text>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <IconInfoCircle size={14} style={{ flexShrink: 0, marginTop: 2, opacity: 0.6 }} />
            <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DEPOSIT_NOTICE, { amount: formatCurrency(summary.depositAmount, summary.hostCurrencyCode, language) })}
            </Text>
          </div>
        </div>
      )}
    </Card>
  );
};
